import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import { Rocket } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { type HomeCategoryKey } from "services/adminApi"
import { type HomeState, type HomeStep } from "@/features/home/homeState"
import { CategoryOptionRow, SELECTABLE_OPTIONS } from "./categoryOptions"

interface OnboardingCardProps {
  onAddProduct: (category: HomeCategoryKey) => void
  onPublish: () => void
  onSelectCategory: (category: HomeCategoryKey) => void
  onViewDemo: () => void
  state: HomeState
}

const STEP_LABEL_KEYS: Record<HomeStep, string> = {
  1: "home.onboarding.steps.category",
  2: "home.onboarding.steps.product",
  3: "home.onboarding.steps.demo",
  4: "home.onboarding.steps.publish",
}

const STEPS: HomeStep[] = [1, 2, 3, 4]

export const OnboardingCard = ({
  onAddProduct,
  onPublish,
  onSelectCategory,
  onViewDemo,
  state,
}: OnboardingCardProps) => {
  const { t } = useTranslation()
  const [pickedCategory, setPickedCategory] = useState<HomeCategoryKey | "">("")

  const { currentStep } = state
  const isSetupStarted = currentStep >= 3
  const selectValue = pickedCategory || state.selectedCategories[0] || ""
  const selectedOption = SELECTABLE_OPTIONS.find((option) => option.key === selectValue)

  const handleCategoryChange = (value: string) => {
    const category = value as HomeCategoryKey
    setPickedCategory(category)
    // The sidebar gains the category immediately (design: choose → nav updates)
    if (!state.selectedCategories.includes(category)) onSelectCategory(category)
  }

  return (
    <section
      className="flex flex-col items-center justify-center rounded-lg border border-empty-tile bg-card px-4 py-14"
      data-testid="home-onboarding-card"
    >
      <div className="flex flex-col items-center gap-6">
        <div className="flex size-[112px] items-center justify-center rounded-[34px] bg-empty-tile">
          <Rocket className="size-[72px]" strokeWidth={1.5} aria-hidden />
        </div>

        <div className="flex flex-col items-center gap-2 text-center">
          <h2 className="text-2xl font-semibold tracking-tight" data-testid="home-onboarding-title">
            {isSetupStarted ? t("home.onboarding.completeTitle") : t("home.onboarding.startTitle")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {isSetupStarted
              ? t("home.onboarding.completeSubtitle")
              : t("home.onboarding.startSubtitle")}
          </p>
        </div>

        <div className="flex items-start" data-testid="home-onboarding-stepper">
          {STEPS.map((step, index) => (
            <Fragment key={step}>
              {index > 0 && (
                <div
                  className={cn(
                    "mt-[15px] h-0.5 w-[100px]",
                    currentStep > step - 1 ? "bg-foreground" : "bg-empty-tile"
                  )}
                />
              )}
              <div className="flex w-8 flex-col items-center gap-2">
                <div
                  data-testid={`home-step-${step}`}
                  data-state={
                    step < currentStep ? "done" : step === currentStep ? "current" : "upcoming"
                  }
                  className={cn(
                    "flex size-8 items-center justify-center rounded-full border text-base",
                    step <= currentStep
                      ? "border-foreground bg-foreground text-background"
                      : "border-empty-tile bg-card text-foreground",
                    step === currentStep && "border-vto-accent"
                  )}
                >
                  {step}
                </div>
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {t(STEP_LABEL_KEYS[step])}
                </span>
              </div>
            </Fragment>
          ))}
        </div>

        {currentStep <= 2 && (
          <Select value={selectValue} onValueChange={handleCategoryChange}>
            {/* Design 15218:46927: the collapsed label stays in full foreground,
                unlike the shared muted placeholder convention. */}
            <SelectTrigger
              className="w-60 data-[placeholder]:text-foreground"
              data-testid="home-category-select"
            >
              {/* Collapsed trigger shows the plain label (design 15218:47099);
                  the icon+hint row is for the open list only. */}
              <SelectValue placeholder={t("home.onboarding.categoryPlaceholder")}>
                {selectedOption ? t(selectedOption.labelKey) : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
                  {t("home.onboarding.groupTryOn")}
                </SelectLabel>
                {SELECTABLE_OPTIONS.filter((option) => option.group === "tryon").map((option) => (
                  <SelectItem
                    key={option.key}
                    value={option.key}
                    data-testid={`home-category-option-${option.key}`}
                  >
                    <CategoryOptionRow option={option} />
                  </SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
                  {t("home.onboarding.group3d")}
                </SelectLabel>
                {SELECTABLE_OPTIONS.filter((option) => option.group === "3d").map((option) => (
                  <SelectItem
                    key={option.key}
                    value={option.key}
                    data-testid={`home-category-option-${option.key}`}
                  >
                    <CategoryOptionRow option={option} />
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}

        {currentStep <= 2 ? (
          <Button
            className="w-60 bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent"
            size="lg"
            disabled={!selectValue}
            onClick={() => selectValue && onAddProduct(selectValue)}
            data-testid="home-onboarding-cta"
          >
            {selectValue ? t("home.onboarding.ctaAddProduct") : t("home.onboarding.ctaLocked")}
          </Button>
        ) : (
          <Button
            className="w-60 bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent"
            size="lg"
            onClick={currentStep === 3 ? onViewDemo : onPublish}
            data-testid="home-onboarding-cta"
          >
            {currentStep === 3 ? t("home.onboarding.ctaViewDemo") : t("home.onboarding.ctaPublish")}
          </Button>
        )}
      </div>
    </section>
  )
}
